/* Prepare nearby desktop hover images; retain the main image until decoding succeeds. */
(() => {
  const placeholder = /\/shopifycloud\/storefront\/assets\/no-image-/i;
  const desktopHover = () => window.matchMedia('(min-width: 768px) and (hover: hover) and (pointer: fine)').matches;
  const inFlight = new WeakMap();
  const loadScene = (card, priority = 'high') => {
    const image = card?.querySelector('img[data-bestek-scene-src]');
    const layer = image?.closest('[data-bestek-hover-state]');
    if (!image || !layer) return Promise.resolve(false);
    if (layer.dataset.bestekHoverState === 'ready') return Promise.resolve(true);
    if (layer.dataset.bestekHoverState === 'loading') {
      if (priority === 'high') image.fetchPriority = 'high';
      return inFlight.get(image) || Promise.resolve(false);
    }
    if (layer.dataset.bestekHoverState !== 'pending') return Promise.resolve(false);
    const source = image.getAttribute('data-bestek-scene-src');
    if (!source || placeholder.test(source)) {
      layer.dataset.bestekHoverState = 'failed';
      return Promise.resolve(false);
    }
    layer.dataset.bestekHoverState = 'loading';
    const pending = new Promise(resolve => {
      let settled = false;
      const finish = ok => {
        if (settled) return;
        settled = true;
        image.removeEventListener('load', loaded);
        image.removeEventListener('error', failed);
        const current = image.getAttribute('data-bestek-scene-src') === source && image.getAttribute('src') === source;
        const valid = current && ok && image.naturalWidth > 1 && !placeholder.test(image.currentSrc || image.src);
        if (current) layer.dataset.bestekHoverState = valid ? 'ready' : 'failed';
        resolve(valid);
      };
      const loaded = () => {
        if (typeof image.decode === 'function') image.decode().then(() => finish(true), () => finish(false));
        else finish(true);
      };
      const failed = () => finish(false);
      image.addEventListener('load', loaded);
      image.addEventListener('error', failed);
      image.loading = 'eager';
      image.fetchPriority = priority;
      image.src = source;
      if (image.complete && image.naturalWidth > 0) loaded();
    });
    inFlight.set(image, pending);
    pending.finally(() => inFlight.delete(image));
    return pending;
  };
  const intent = event => { if (desktopHover()) loadScene(event.target?.closest?.('.product-card'), 'high'); };
  document.addEventListener('pointerover', intent, {passive:true});
  document.addEventListener('mouseover', intent, {passive:true});
  document.addEventListener('focusin', intent);

  const connection = navigator.connection;
  const canWarm = () => desktopHover() && !connection?.saveData && !/^(slow-)?2g$/.test(connection?.effectiveType || '');
  if (!('IntersectionObserver' in window) || !canWarm()) return;
  const queue = new Set();
  const seen = new WeakSet();
  let active = 0;
  const drain = () => {
    if (!canWarm()) { queue.clear(); return; }
    while (active < 2 && queue.size) {
      const card = queue.values().next().value;
      queue.delete(card);
      if (!card.isConnected) continue;
      observer.unobserve(card);
      active += 1;
      loadScene(card, 'low').finally(() => { active -= 1; drain(); });
    }
  };
  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) queue.add(entry.target);
      else queue.delete(entry.target);
    });
    drain();
  }, {rootMargin:'600px 0px'});
  const observe = root => {
    const cards = [...root.querySelectorAll('.product-card')];
    if (root.matches?.('.product-card')) cards.unshift(root);
    cards.forEach(card => {
      if (seen.has(card) || !card.querySelector('[data-bestek-hover-state="pending"] img[data-bestek-scene-src]')) return;
      seen.add(card); observer.observe(card);
    });
  };
  const boot = () => setTimeout(() => {
    const start = () => {
      observe(document);
      const release = root => {
        if (root.nodeType !== 1) return;
        const cards = [root, ...root.querySelectorAll('.product-card')];
        cards.forEach(card => { observer.unobserve(card); queue.delete(card); seen.delete(card); });
      };
      const mutations = new MutationObserver(records => records.forEach(record => {
        record.removedNodes.forEach(node => release(node));
        record.addedNodes.forEach(node => { if (node.nodeType === 1) observe(node); });
      }));
      mutations.observe(document.getElementById('MainContent') || document.body, {childList:true,subtree:true});
      document.addEventListener('shopify:section:load', event => observe(event.target));
      document.addEventListener('shopify:section:unload', event => release(event.target));
    };
    if ('requestIdleCallback' in window) requestIdleCallback(start, {timeout:1500});
    else start();
  }, 800);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, {once:true});
  else boot();
})();
